import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { NumberInputHandlers } from '@mantine/core';
import { Group, NumberInput, Stack, useMantineTheme } from '@mantine/core';
import { IconPlus, IconMinus, IconAlertCircle } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { match, P } from 'ts-pattern';

import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import PayWithCreditsToggle from '~/domains/design-system/components/PayWithCreditsToggle/PayWithCreditsToggle';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import { Routes } from '~/domains/common/constants/routes';
import { CurrencyConvertor } from '~/components/utils/formatters';
import { Button } from '~/domains/design-system/Button';
import { PromoCard } from '~/domains/design-system/components/PromoCard/PromoCard';
import type { PromoCardProps } from '~/domains/promotions/types';

import ContestCard from '../../ContestCard/ContestCard';
import dt from '../../../testing.const';
import type { EnterContestSource } from '../ConnectedEnterContestModal';

import EntryZipVerificationCard from './EntryZipVerificationCard';
import useEntryZipGate from './useEntryZipGate';
import useStyles from './EnterContestScreen.styles';
import ContestCTA, { type CTAVariant } from './ContestCTA';

function EnterContestScreen({
  cashBalance,
  contest,
  onDepositClick,
  onCreateEntries,
  onClose,
  errorMessage,
  isCreateEntriesReady,
  creditBalance,
  payWithCredits,
  onPayWithCreditsChange,
  source = 'default',
  promoCardProps,
  voucherError,
  voucherValueInDollars,
}: {
  cashBalance: number;
  contest: FullContest | Contest;
  onDepositClick: () => void;
  onCreateEntries: (entriesCount: number, payWithCredits: boolean, zipCode?: string) => void;
  onClose: () => void;
  errorMessage?: string;
  isCreateEntriesReady: boolean;
  creditBalance?: number;
  payWithCredits: boolean;
  onPayWithCreditsChange: (payWithCredits: boolean) => void;
  source?: EnterContestSource;
  promoCardProps?: PromoCardProps;
  voucherError?: string;
  voucherValueInDollars?: number;
}) {
  const { t } = useTranslation('contest');
  const theme = useMantineTheme();
  const handlers = useRef<NumberInputHandlers>();

  const [value, setValue] = useState(1);
  const isAnonCreateEntryMode = source === 'anonCreateEntry';
  const entriesCount = isAnonCreateEntryMode ? 1 : value;
  const maxEntries =
    contest.entries.max === 0 && contest.entries.is_unlimited_entries
      ? Infinity
      : contest.entries.max;
  const maxAllowedEntries = useMemo(
    () =>
      contest?.entries
        ? Math.min(
            contest.entries.max_per_user - contest.entries.user,
            maxEntries - contest.entries.filled
          )
        : 0,
    [contest, maxEntries]
  );

  const singleEntryFee = (contest?.entry_fee ?? 0) / 100;
  const entryFee = singleEntryFee * entriesCount;
  const voucherValue = voucherValueInDollars ?? 0;
  const totalEntryFee = voucherValue >= singleEntryFee ? entryFee - singleEntryFee : entryFee;
  const hasVoucherEntryWarning = !!promoCardProps && entriesCount > 1;
  const hasVoucherValueWarning = voucherValue > entryFee;

  // Check if combined balance (cash + credits) is sufficient
  // cashBalance is required (type: number), creditBalance is optional (type: number | undefined)
  const combinedBalance = useMemo(
    () => cashBalance + (creditBalance ?? 0),
    [cashBalance, creditBalance]
  );

  // Entry is allowed if combined balance covers the fee
  const hasSufficientCombinedBalance = combinedBalance >= totalEntryFee;

  // With combined balance logic, the toggle is enabled as long as combined balance is sufficient
  // The backend will tap into the other balance type if needed
  const canUseCreditsToggle = hasSufficientCombinedBalance && (creditBalance ?? 0) > 0;

  // Determine the credits toggle label:
  // - "Use Credits" when credits can fully cover the fee, OR when insufficient combined funds
  // - "Use Credits first" when credits < entry fee but combined balance is sufficient
  //   (meaning credits will be used first, then cash for the remainder)
  const canCreditsFullyCover = (creditBalance ?? 0) >= totalEntryFee;
  const creditsToggleLabel =
    canCreditsFullyCover || !hasSufficientCombinedBalance
      ? t('common.enterContestModal.enterContest.useCredits')
      : t('common.enterContestModal.enterContest.useCreditsFirst');

  // Show credits selection if user has any credits
  const showCreditsSelection =
    (creditBalance ?? 0) > 0 && !contest.settings?.entertainmentOnlySettings?.enabled;

  // Determine if deposit button should be shown
  // If using credits (toggle checked), check combined balance
  // If not using credits (toggle unchecked), check cash only
  const canAffordEntry = payWithCredits
    ? hasSufficientCombinedBalance
    : cashBalance >= totalEntryFee;
  const shouldShowDeposit = !canAffordEntry;

  const isEntertainmentOnly = contest.settings?.entertainmentOnlySettings?.enabled;
  const {
    zipCode,
    isZipTermsChecked,
    isZipGateRequired,
    isZipGateUnlocked,
    zipCodeError,
    maxZipLength,
    handleZipCodeChange,
    setIsZipTermsChecked,
  } = useEntryZipGate(contest);
  const handleCreateEntries = useCallback(() => {
    onCreateEntries(entriesCount, payWithCredits, isZipGateRequired ? zipCode : undefined);
  }, [onCreateEntries, entriesCount, payWithCredits, isZipGateRequired, zipCode]);

  const ctaVariant = match({ hasError: !!errorMessage || !!voucherError, shouldShowDeposit })
    .returnType<CTAVariant>()
    .with({ hasError: true }, () => 'error' as const)
    .with({ shouldShowDeposit: true }, () => 'deposit' as const)
    .otherwise(() => 'enter' as const);

  const { classes } = useStyles();

  return (
    <>
      <Stack align="center" ta="center" spacing={theme.other.spacing._4}>
        <Title variant="subhead-x-large" color="gray2.7" data-test-id={dt.enterContestModal.title}>
          {t('common.enterContestModal.enterContest.title')}
        </Title>

        {!isEntertainmentOnly && (
          <Text
            variant="subhead-medium"
            color={hasSufficientCombinedBalance ? theme.colors.gray2[3] : theme.colors.red[3]}
          >
            {t('common.enterContestModal.enterContest.cashBalance', {
              balance: CurrencyConvertor(cashBalance),
            })}
          </Text>
        )}

        {match({ errorMessage, maxPerUser: contest.entries.max_per_user })
          .returnType<React.ReactNode>()
          .with({ errorMessage: P.string.minLength(1) }, ({ errorMessage: msg }) => (
            <div className={classes.alertContainer}>
              <Text variant="body-medium" tag="div">
                {msg.charAt(0).toUpperCase() + msg.slice(1)}
              </Text>
            </div>
          ))
          .with({ maxPerUser: P.number.gt(1) }, () => (
            <Text variant="body-medium" color={theme.colors.gray2[4]}>
              {t('common.enterContestModal.enterContest.text')}
            </Text>
          ))
          .otherwise(() => null)}
      </Stack>

      {promoCardProps && (
        <div className={classes.promoCardSection}>
          <div className={classes.promoCardContainer}>
            <PromoCard {...promoCardProps} variant="default" showBorder isSelected />
          </div>

          {voucherError && (
            <div className={classes.voucherWarningContainer}>
              <IconAlertCircle size={16} color={theme.colors.red[3]} />
              <div>
                <Text variant="subhead-x-small" tag="div" color={theme.colors.red[3]}>
                  {t('common.enterContestModal.enterContest.voucherInvalid')}
                </Text>
                <Text variant="body-small" tag="div" color={theme.colors.red[3]}>
                  {voucherError.charAt(0).toUpperCase() + voucherError.slice(1)}
                </Text>
              </div>
            </div>
          )}

          {(hasVoucherValueWarning || hasVoucherEntryWarning) && (
            <div className={classes.voucherWarningContainer}>
              <IconAlertCircle size={16} color={theme.colors.gray2[3]} />
              <Text variant="subhead-x-small" ta="left">
                {t(
                  hasVoucherEntryWarning
                    ? 'common.enterContestModal.enterContest.voucherWarningMultiEntry'
                    : 'common.enterContestModal.enterContest.voucherWarningHigherValue',
                  {
                    voucherValue: CurrencyConvertor(voucherValue),
                    entryFee: CurrencyConvertor(entryFee),
                  }
                )}
              </Text>
            </div>
          )}
        </div>
      )}

      <div className={classes.contestCardSection}>
        <div className={classes.contestCardContainer}>
          <ContestCard
            contest={contest}
            isContestThumbnailShown
            isStatic
            isCardMenuVisible={false}
          />
        </div>
      </div>

      {isZipGateRequired && (
        <EntryZipVerificationCard
          zipCode={zipCode}
          isZipTermsChecked={isZipTermsChecked}
          zipCodeError={zipCodeError}
          maxZipLength={maxZipLength}
          termsUrl={Routes.entryZipTerms}
          onZipCodeChange={handleZipCodeChange}
          onZipTermsChange={setIsZipTermsChecked}
        />
      )}

      <div className={!isZipGateUnlocked ? classes.disabledContent : undefined}>
        {contest.entries.max_per_user > 1 && !isAnonCreateEntryMode && (
          <Stack spacing={theme.other.spacing._4} w="100%" align="center">
            <Group spacing={theme.other.spacing._16}>
              <Button
                size="medium-icon"
                variant="tertiary-fill"
                onClick={() => handlers.current.decrement()}
                data-test-id={dt.enterContestModal.action.decrement}
                disabled={maxAllowedEntries === 1 || value === 1}
              >
                <IconMinus size={20} color="currentColor" />
              </Button>
              <NumberInput
                hideControls
                value={value}
                onChange={setValue}
                data-test-id={dt.enterContestModal.action.numberOfEntries}
                handlersRef={handlers}
                min={1}
                max={maxAllowedEntries}
                classNames={{ input: classes.numberInput }}
                disabled={maxAllowedEntries === 1}
              />
              <Button
                size="medium-icon"
                variant="tertiary-fill"
                onClick={() => handlers.current.increment()}
                data-test-id={dt.enterContestModal.action.increment}
                disabled={maxAllowedEntries === 1 || value === maxAllowedEntries}
              >
                <IconPlus size={20} color="currentColor" />
              </Button>
            </Group>
            <Text variant="body-medium" color={theme.colors.gray2[3]}>
              {t('common.enterContestModal.enterContest.maxEntries__hasPlural', {
                count: maxAllowedEntries,
              })}
            </Text>
            {!hasSufficientCombinedBalance && !isEntertainmentOnly && (
              <Text variant="subhead-small" color={theme.colors.red[3]} ta="center">
                {t('common.enterContestModal.enterContest.notEnoughCash')}
              </Text>
            )}
          </Stack>
        )}

        {showCreditsSelection && (
          <div className={classes.creditsSelectionSection}>
            <PayWithCreditsToggle
              checked={payWithCredits}
              creditBalance={creditBalance}
              hasSufficientCredits={canUseCreditsToggle}
              hasVoucher={!!promoCardProps}
              onCheckedChange={onPayWithCreditsChange}
              variant="light"
              label={creditsToggleLabel}
              insufficientCreditsMessage={
                !canAffordEntry
                  ? t('common.enterContestModal.enterContest.insufficientFunds')
                  : undefined
              }
            />
          </div>
        )}

        <div className={classes.footer}>
          <ContestCTA
            key={promoCardProps ? 'promoCard' : 'noPromoCard'}
            variant={ctaVariant}
            isCreateEntriesReady={isCreateEntriesReady}
            isZipGateUnlocked={isZipGateUnlocked}
            isEntertainmentOnly={!!isEntertainmentOnly}
            value={entriesCount}
            totalEntryFee={totalEntryFee}
            classes={classes}
            onDepositClick={onDepositClick}
            onCreateEntries={handleCreateEntries}
            onClose={onClose}
          />
        </div>
      </div>
    </>
  );
}

export default EnterContestScreen;
